/**
 * Hook para gerenciamento de RTCPeerConnection
 * Estabelece e gerencia conexão P2P para WebRTC
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import type { UseWebRTCReturn } from './types';
import { ICE_SERVERS_CONFIG } from './types';

// Logger para diagnóstico
const logger = createLogger('useWebRTC');

/**
 * Callback para quando um candidato ICE é gerado localmente
 */
type OnIceCandidateCallback = (candidate: RTCIceCandidateInit) => void;

/**
 * Props do hook useWebRTC
 */
interface UseWebRTCProps {
  /** Callback chamado quando candidato ICE local é gerado */
  onIceCandidate?: OnIceCandidateCallback;
  /** Callback chamado quando estado da conexão muda */
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
}

/**
 * Hook para gerenciamento de RTCPeerConnection
 * 
 * Responsabilidades:
 * - Criar e gerenciar RTCPeerConnection
 * - Configurar ICE servers (STUN apenas nesta fase)
 * - Adicionar/remover tracks de vídeo
 * - Gerenciar estado da conexão
 * - Coletar e processar ICE candidates
 * 
 * @param props Callbacks opcionais para eventos
 * @returns Objeto com estado da conexão e funções de controle
 */
export function useWebRTC(props?: UseWebRTCProps): UseWebRTCReturn {
  const { onIceCandidate, onConnectionStateChange } = props || {};
  
  // Estado da conexão P2P
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState>('new');
  // Stream de vídeo remoto recebido
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  
  // Refs para a conexão e callbacks
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const onIceCandidateRef = useRef(onIceCandidate);
  const onConnectionStateChangeRef = useRef(onConnectionStateChange);

  // Atualiza refs quando callbacks mudam
  useEffect(() => {
    onIceCandidateRef.current = onIceCandidate;
    onConnectionStateChangeRef.current = onConnectionStateChange;
  }, [onIceCandidate, onConnectionStateChange]);

  /**
   * Cria nova RTCPeerConnection se não existir
   */
  const ensureConnection = useCallback(() => {
    if (peerConnectionRef.current) {
      return peerConnectionRef.current;
    }

    logger.info('Criando nova RTCPeerConnection');
    const pc = new RTCPeerConnection(ICE_SERVERS_CONFIG);

    // Handler para ICE candidates locais gerados
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        logger.debug('ICE candidate gerado localmente', {
          type: event.candidate.type,
          protocol: event.candidate.protocol,
        });
        onIceCandidateRef.current?.(event.candidate.toJSON());
      }
    };

    // Handler para mudanças no estado da conexão
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      logger.info('Estado da conexão mudou', { state });
      setConnectionState(state);
      onConnectionStateChangeRef.current?.(state);

      // Limpa recursos se conexão falhou ou fechou
      if (state === 'failed' || state === 'closed') {
        logger.warn('Conexão encerrada ou falhou', { state });
      }
    };

    // Handler para estado de ICE gathering
    pc.onicegatheringstatechange = () => {
      logger.debug('ICE gathering state', { state: pc.iceGatheringState });
    };

    // Handler para estado da conexão ICE
    pc.oniceconnectionstatechange = () => {
      logger.debug('ICE connection state', { state: pc.iceConnectionState });
    };

    // Handler para tracks remotas recebidas
    pc.ontrack = (event) => {
      logger.info('Track remota recebida', {
        kind: event.track.kind,
        streamCount: event.streams.length,
      });

      if (event.streams && event.streams[0]) {
        setRemoteStream(event.streams[0]);
      }
    };

    peerConnectionRef.current = pc;
    return pc;
  }, []);

  /**
   * Cria offer SDP (chamado pelo viewer/atendente)
   * 
   * @returns Promise com o SDP offer gerado
   */
  const createOffer = useCallback(async (): Promise<RTCSessionDescriptionInit> => {
    const pc = ensureConnection();
    
    logger.info('Criando SDP offer');
    const offer = await pc.createOffer({
      offerToReceiveVideo: true, // Queremos receber vídeo
      offerToReceiveAudio: false, // Sem áudio
    });
    
    await pc.setLocalDescription(offer);
    logger.debug('Local description definida (offer)');
    
    return offer;
  }, [ensureConnection]);

  /**
   * Cria answer SDP em resposta a um offer (chamado pelo sharer/cliente)
   * 
   * @param offer O SDP offer recebido do viewer
   * @returns Promise com o SDP answer gerado
   */
  const createAnswer = useCallback(async (offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> => {
    const pc = ensureConnection();
    
    logger.info('Definindo remote description (offer) e criando answer');
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    logger.debug('Local description definida (answer)');
    
    return answer;
  }, [ensureConnection]);

  /**
   * Define a descrição remota (SDP recebido do peer)
   * 
   * @param sdp O SDP (offer ou answer) recebido
   */
  const setRemoteDescription = useCallback(async (sdp: RTCSessionDescriptionInit): Promise<void> => {
    const pc = ensureConnection();
    
    logger.info('Definindo remote description', { type: sdp.type });
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
  }, [ensureConnection]);

  /**
   * Adiciona candidato ICE remoto
   * 
   * @param candidate O candidato ICE recebido do peer
   */
  const addIceCandidate = useCallback(async (candidate: RTCIceCandidateInit): Promise<void> => {
    const pc = peerConnectionRef.current;
    
    if (!pc) {
      logger.warn('Tentativa de adicionar ICE candidate sem conexão ativa');
      return;
    }

    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
      logger.debug('ICE candidate remoto adicionado');
    } catch (err) {
      logger.error('Erro ao adicionar ICE candidate', err);
    }
  }, []);

  /**
   * Adiciona stream local para transmissão (usado pelo sharer/cliente)
   * 
   * @param stream O MediaStream de captura de tela
   */
  const addLocalStream = useCallback((stream: MediaStream): void => {
    const pc = ensureConnection();
    
    logger.info('Adicionando stream local', {
      videoTracks: stream.getVideoTracks().length,
    });

    // Adiciona todas as tracks do stream
    stream.getTracks().forEach(track => {
      pc.addTrack(track, stream);
      logger.debug(`Track ${track.kind} adicionada`);
    });
  }, [ensureConnection]);

  /**
   * Fecha conexão e libera todos os recursos
   */
  const close = useCallback((): void => {
    const pc = peerConnectionRef.current;
    
    if (pc) {
      logger.info('Fechando RTCPeerConnection');
      pc.close();
      peerConnectionRef.current = null;
    }

    setConnectionState('closed');
    setRemoteStream(null);
  }, []);

  /**
   * Cleanup ao desmontar componente
   */
  useEffect(() => {
    return () => {
      if (peerConnectionRef.current) {
        logger.debug('Cleanup: fechando conexão ao desmontar');
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
    };
  }, []);

  return {
    connectionState,
    remoteStream,
    createOffer,
    createAnswer,
    setRemoteDescription,
    addIceCandidate,
    addLocalStream,
    close,
  };
}
